// His sentence per timeline kind (lane B10, slice S18; plan D12; CR-093 "Candidate timeline: source/application through
// HRIS/start/disposition."; CR-094 "Requisition timeline: request through
// approval/posting/intake/panel/offer/filled/hold/cancel/reopen/archive."). `timeline.view` sends each row's kind and a
// sentence key (`timeline.<kind>`); this module words it: one word, or a lead and a word joined by " · ", every word a
// label of ./labels.ts. A span that ended reads "<its word> · Resolved"; a record taken back reads
// "<its word> · Superseded", except a taken-back acceptance, which is his pending "Acceptance reversal".
//
// SENTENCE_TABLE lists every kind B1 projects (spine.vocab TIMELINE_KINDS) and the kinds lanes B3 to B9 register;
// tests/reporting/test_timeline_sentences.py fails when a registered kind has no line here. A kind still missing reads
// "Timeline event" rather than its code.

import type { LabelDef, Tone } from "@/mc/labels";
import { isStatusWord, toneOf } from "@/mc/labels";
import { EVENT_WORD_DEFS as W, TIMELINE_LABEL_DEFS } from "./labels";

type Words = readonly [LabelDef, ...LabelDef[]];

/** kind -> its words, lead first. Keys are string literals so the backend test can read them. */
export const SENTENCE_TABLE = {
  // ---- B1: the request (enums.REQUEST_EVENT_KINDS) ----
  "request.created": [W.hiringRequest, W.draft],
  "request.submitted": [W.hiringRequest, W.submitted],
  "request.question_raised": [W.hiringRequest, W.returned],
  "request.answered": [W.hiringRequest, W.submitted],
  "request.returned": [W.hiringRequest, W.returned],
  "request.resubmitted": [W.hiringRequest, W.submitted],
  "request.approved": [W.hiringRequest, W.approved],
  "request.withdrawn": [W.hiringRequest, W.withdrawn],
  "request.marked_dormant": [W.hiringRequest, W.dormant],
  "request.converted": [W.reqCreationPending],
  // B2's request events (S01 routing and certification; the approval's parallel start), as B2's History reads them
  "request.routed_to_hm": [W.hiringRequest, W.pendingHmCertification],
  "request.hm_certified": [W.hiringRequest, W.hmCertified],
  "request.ta_started": [W.coordinatorCreatesPosts, W.taManagerAssignsRecruiter],
  "request.comp_requested": [W.compensationStartsInParallel],
  // ---- B1: the milestones reached (enums.MILESTONE_KEYS) ----
  "milestone.request_approved": [W.requestApproved],
  "milestone.req_creation_pending": [W.reqCreationPending],
  "milestone.created": [W.created],
  "milestone.posted": [W.posted],
  "milestone.recruiter_recommended": [W.recruiter, W.recommended],
  "milestone.pending_ta_manager": [W.recruiter, W.pendingTaManager],
  "milestone.recruiter_assigned": [W.recruiter, W.assigned],
  "milestone.comp_pending": [W.compensationReview, W.pending],
  "milestone.comp_in_review": [W.compensationReview, W.inReview],
  "milestone.comp_finalized": [W.compensationReview, W.compFinalized],
  "milestone.hold_pending": [W.requestHold],
  "milestone.on_hold": [W.onHold],
  "milestone.cancellation_pending": [W.cancellationPending],
  "milestone.cancelled": [W.cancelled],
  "milestone.reopen_pending": [W.reopenPending],
  "milestone.reopened": [W.reopened],
  "milestone.offer_accepted": [W.offerAccepted],
  "milestone.background_check": [W.backgroundCheck],
  "milestone.ready_for_hris": [W.readyForHris],
  "milestone.hris_hired": [W.hrisHired],
  "milestone.started": [W.start],
  "milestone.filled": [W.filled],
  "milestone.archived": [W.archived],
  // ---- B1: a span that ended (enums.MILESTONE_SPANS) ----
  "milestone.hold_pending.ended": [W.requestHold, W.resolved],
  "milestone.on_hold.ended": [W.onHold, W.resolved],
  "milestone.cancellation_pending.ended": [W.cancellationPending, W.resolved],
  "milestone.reopen_pending.ended": [W.reopenPending, W.resolved],
  "milestone.comp_in_review.ended": [W.compensationReview, W.inReview, W.resolved],
  // ---- B1: a milestone taken back ----
  "milestone.request_approved.reversed": [W.requestApproved, W.superseded],
  "milestone.req_creation_pending.reversed": [W.reqCreationPending, W.superseded],
  "milestone.created.reversed": [W.created, W.superseded],
  "milestone.posted.reversed": [W.posted, W.superseded],
  "milestone.recruiter_recommended.reversed": [W.recruiter, W.recommended, W.superseded],
  "milestone.pending_ta_manager.reversed": [W.recruiter, W.pendingTaManager, W.superseded],
  "milestone.recruiter_assigned.reversed": [W.recruiter, W.assigned, W.superseded],
  "milestone.comp_pending.reversed": [W.compensationReview, W.pending, W.superseded],
  "milestone.comp_in_review.reversed": [W.compensationReview, W.inReview, W.superseded],
  "milestone.comp_finalized.reversed": [W.compensationReview, W.compFinalized, W.superseded],
  "milestone.hold_pending.reversed": [W.requestHold, W.superseded],
  "milestone.on_hold.reversed": [W.onHold, W.superseded],
  "milestone.cancellation_pending.reversed": [W.cancellationPending, W.superseded],
  "milestone.cancelled.reversed": [W.cancelled, W.superseded],
  "milestone.reopen_pending.reversed": [W.reopenPending, W.superseded],
  "milestone.reopened.reversed": [W.reopened, W.superseded],
  "milestone.offer_accepted.reversed": [W.acceptanceReversal],
  "milestone.background_check.reversed": [W.backgroundCheck, W.superseded],
  "milestone.ready_for_hris.reversed": [W.readyForHris, W.superseded],
  "milestone.hris_hired.reversed": [W.hrisHired, W.superseded],
  "milestone.started.reversed": [W.start, W.superseded],
  "milestone.filled.reversed": [W.filled, W.superseded],
  "milestone.archived.reversed": [W.archived, W.superseded],
  // ---- B1: the checkpoints the milestones do not already say (posting is Posted, acceptance Offer Accepted) ----
  "checkpoint.sourcing": [W.sourcing],
  "checkpoint.first_screen": [W.recruiterScreen],
  "checkpoint.hm_interview": [W.hmInterview],
  "checkpoint.onsite_interviews": [W.finalInterviewLoop],
  "checkpoint.offer": [W.offer],
  "checkpoint.sourcing.superseded": [W.sourcing, W.superseded],
  "checkpoint.first_screen.superseded": [W.recruiterScreen, W.superseded],
  "checkpoint.hm_interview.superseded": [W.hmInterview, W.superseded],
  "checkpoint.onsite_interviews.superseded": [W.finalInterviewLoop, W.superseded],
  "checkpoint.offer.superseded": [W.offer, W.superseded],
  // ---- B1: the application ----
  "application.stage_changed": [W.stage],
  "application.decision": [W.candidateDecision],
  "application.held": [W.held],
  "application.hold_released": [W.held, W.resolved],
  "application.withdrawn": [W.withdrawn],
  "application.reinstated": [W.withdrawn, W.superseded],
  // ---- B1: the offer (enums.OFFER_EVENT_KINDS; never an amount) ----
  "offer.comp_recorded": [W.offerPackage],
  "offer.comp_revised": [W.revisedOffer],
  "offer.verbal_extended": [W.verbalExtended],
  "offer.letter_sent": [W.writtenSent],
  "offer.negotiating": [W.negotiation],
  "offer.accepted": [W.offerAccepted],
  "offer.declined": [W.offerDecline],
  "offer.withdrawn": [W.offer, W.withdrawn],
  "offer.acceptance_reversed": [W.acceptanceReversal],
  // ---- B3 (candidate master, talent community, slate, screen) — registered when B3 merges ----
  "candidate.created": [W.candidateProfile],
  "candidate.resume_version": [W.resumeVersion],
  "candidate.parse_confirmed": [W.humanConfirmation],
  "candidate.contact_changed": [W.candidateProfile, W.reviewEdit],
  "candidate.profile_updated": [W.candidateProfile, W.reviewEdit],
  "candidate.owner_changed": [W.candidateProfile, W.owner],
  "candidate.application_linked": [W.candidateApplication],
  "candidate.match_resolved": [W.potentialMatch, W.resolved],
  "candidate.first_contact": [W.firstContact],
  "candidate.merged": [W.merged],
  "candidate.unmerged": [W.recover],
  "community.added": [W.addToTalentCommunity],
  "community.removed": [W.talentCommunity, W.superseded],
  "community.touched": [W.talentCommunity, W.lastContact],
  "application.internal_attested": [W.selfAttestation],
  "slate.submitted": [W.candidateSlate, W.submitted],
  "slate.opened": [W.candidateSlate, W.inHmReview],
  "slate.completed": [W.candidateSlate, W.completed],
  "slate.overdue": [W.candidateSlate, W.overdue],
  "slate.reopened": [W.candidateSlate, W.reopened],
  "screen.outcome": [W.recruiterScreen, W.outcome],
  // ---- B4 (the interview plan; the assessment) — registered when B4 merges ----
  "plan.intake_held": [W.intake, W.completed],
  "plan.framework_loaded": [W.loadFramework],
  "plan.validation_failed": [W.interviewPlan, W.validationFailed],
  "plan.ready": [W.interviewPlan, W.ready],
  "plan.variant_set": [W.interviewPlan, W.customize],
  "plan.finalized": [W.interviewPlan, W.planFinalized],
  "plan.reopened": [W.interviewPlan, W.reopened],
  "assessment.sent": [W.assessment, W.sent],
  "assessment.submitted": [W.assessment, W.submitted],
  "assessment.reviewed": [W.assessment, W.reviewed],
  "assessment.completed": [W.assessment, W.completed],
  "assessment.withdrawn": [W.assessment, W.withdrawn],
  // ---- B5 (scheduling; the meeting type, when the row names it, replaces the lead) — registered when B5 merges ----
  "scheduling.requested": [W.schedulingRequest, W.requested],
  "scheduling.scheduled": [W.schedulingRequest, W.scheduled],
  "scheduling.rescheduled": [W.schedulingRequest, W.rescheduled],
  "scheduling.reschedule_requested": [W.schedulingRequest, W.rescheduleRequested],
  "scheduling.completed": [W.schedulingRequest, W.completed],
  "scheduling.cancelled": [W.schedulingRequest, W.cancelled],
  // ---- B6 (packets, acknowledgment, panel change) — registered when B6 merges ----
  "packet.generated": [W.packet, W.generated],
  "packet.reviewed": [W.packet, W.reviewed],
  "packet.released": [W.packet, W.released],
  "panel.ack_accepted": [W.acknowledgment, W.accepted],
  "panel.ack_declined": [W.acknowledgment, W.ackDeclined],
  "panel.ack_nonresponse": [W.acknowledgment, W.nonresponse],
  "panel_change.submitted": [W.panelChange, W.panelChangeSubmitted],
  "panel_change.blocked": [W.panelChange, W.panelChangeBlocked],
  "panel_change.approved": [W.panelChange, W.panelChangeApproved],
  "panel_change.rejected": [W.panelChange, W.panelChangeRejected],
  "panel_change.completed": [W.panelChange, W.panelChangeCompleted],
  // ---- B7 (scorecards, summary, debrief, alternates) — registered when B7 merges ----
  "scorecard.submitted": [W.scorecard, W.scorecardSubmitted],
  "scorecard.revised": [W.scorecard, W.ownScoreRevision],
  "scorecard.waived": [W.scorecard, W.waived],
  "scorecard.corrected": [W.scorecard, W.administrativeCorrection],
  "concern_flag.raised": [W.concernFlag],
  "debrief.revealed": [W.formalDebrief, W.reveal],
  "debrief.revised_in_room": [W.formalDebrief, W.ownScoreRevision],
  "summary.released": [W.candidateSummary, W.released],
  "debrief.search_outcome": [W.formalDebrief, W.outcomeRecorded],
  "alternate.designated": [W.alternateDesignation],
  "alternate.released": [W.alternateDesignation, W.resolved],
  "alternate.promoted": [W.alternateDesignation, W.primary],
  "exception_request.requested": [W.additionalInterviewException, W.requested],
  "exception_request.agreed": [W.additionalInterviewException, W.approved],
  // ---- B8 (offer approvals, counters, decisions; the reference check) — registered when B8 merges ----
  "offer.approval_recorded": [W.offerApproval],
  "offer.counter_recorded": [W.counter],
  "offer.decision_recorded": [W.offerCandidateDecision],
  "reference.required": [W.referenceCheck, W.required],
  "reference.not_required": [W.referenceCheck, W.notRequired],
  "reference.requested": [W.referenceCheck, W.requested],
  "reference.contact_recorded": [W.referenceCheck, W.inProgress],
  "reference.completed": [W.referenceCheck, W.completed],
  "reference.unable_to_complete": [W.referenceCheck, W.unableToComplete],
  "reference.waived": [W.referenceCheck, W.waived],
  // ---- B9 (handoff and HRIS) — registered when B9 merges ----
  "handoff.item_recorded": [W.handoffChecklist],
  "hris.confirmed": [W.hrisConfirmation],
  "hris.corrected": [W.hrisConfirmation, W.administrativeCorrection],
} as const satisfies Readonly<Record<string, Words>>;

export type TimelineKind = keyof typeof SENTENCE_TABLE;

const TABLE: Readonly<Record<string, Words>> = SENTENCE_TABLE;

/** kind -> the word the sentence ends with (the event's own word; a lead, when there is one, comes before it). */
export const SENTENCES: Readonly<Record<string, LabelDef>> = Object.freeze(
  Object.fromEntries(Object.entries(TABLE).map(([kind, words]) => [kind, words[words.length - 1]!])),
);

/** kind -> the words before the event's own word ("Compensation review" before "Pending"); absent when none. */
export const LEADS: Readonly<Record<string, readonly LabelDef[]>> = Object.freeze(
  Object.fromEntries(Object.entries(TABLE).filter(([, words]) => words.length > 1).map(([kind, words]) => [kind, words.slice(0, -1)])),
);

/** Joins words the way every sentence here is joined. */
export const SEPARATOR = " · ";

/** A kind as `timeline.view` sends it: the bare kind or its sentence key (`timeline.<kind>`). */
export function kindOf(kindOrKey: string): string {
  return kindOrKey.startsWith("timeline.") ? kindOrKey.slice("timeline.".length) : kindOrKey;
}

/** A record's own entry for a key the server sent (never one of Object.prototype's names). */
export function ownEntry<T>(record: Readonly<Record<string, T>>, key: string | undefined): T | undefined {
  return key !== undefined && Object.hasOwn(record, key) ? record[key] : undefined;
}

/** The words of a kind, lead first; a kind with no line reads as the one word "Timeline event". */
export function wordsFor(kindOrKey: string): readonly LabelDef[] {
  return ownEntry(TABLE, kindOf(kindOrKey)) ?? [TIMELINE_LABEL_DEFS.timelineEvent];
}

/** His sentence for a timeline kind (or its sentence key). */
export function sentenceFor(kindOrKey: string): string {
  return wordsFor(kindOrKey).map((word) => word.text).join(SEPARATOR);
}

// ---- a row's own codes sharpen its sentence ----------------------------------------------------------------------------

/** The application stage a stage change reached (enums.APPLICATION_STAGE), read with his words (G §7, §8, §11, §12). */
export const STAGE_WORDS: Readonly<Record<string, LabelDef>> = {
  sourced: W.sourced,
  qualified: W.qualified,
  in_review: W.pendingReview,
  screen: W.recruiterScreen,
  screened: W.pendingHmSelection,
  hm_round: W.hmInterview,
  hm_assessed: W.candidateDecision,
  panel: W.finalInterviewLoop,
  debrief: W.formalDebrief,
  offer_approved: W.primary,
  alternate: W.alternate,
  offer: W.offer,
  hired: W.offerAccepted,
  // B9: acceptance and the HRIS hire are stages of their own ("Offer Accepted -> Ready for HRIS -> HRIS Hired")
  offer_accepted: W.offerAccepted,
  hris_hired: W.hrisHired,
  declined: W.declined,
  reneged: W.renege,
  held: W.held,
  not_selected: W.dispositioned,
  passed_on: W.passed,
  withdrawn: W.withdrawn,
  dispositioned: W.dispositioned,
};

/** A recorded decision's value (enums.DECISION_VALUES), where he has a word for it. */
export const DECISION_WORDS: Readonly<Record<string, LabelDef>> = {
  advance: W.advance,
  hold: W.hold,
  pass: W.pass,
  extend_offer: W.primary,
  additional_round: W.additionalInterviewException,
  hold_as_alternate: W.alternate,
};

/** A scheduling request's meeting type (B5 MEETING_TYPES), his meeting-template words (G §9). */
export const MEETING_WORDS: Readonly<Record<string, LabelDef>> = {
  intake: W.meetingIntake,
  recruiter_screen: W.meetingRecruiterScreen,
  hm_interview: W.meetingHmInterview,
  final_loop: W.meetingFinalInterviewLoop,
  debrief: W.meetingDebrief,
  offer_call: W.meetingOfferCall,
};

/** What of a row the sentence reads: its kind and its codes (facts are codes and ids only). */
export interface SentenceRow {
  kind: string;
  facts?: Readonly<Record<string, unknown>> | null | undefined;
}

const code = (facts: SentenceRow["facts"], key: string): string | undefined => {
  const value = facts?.[key];
  return typeof value === "string" ? value : undefined;
};

/** The words of one row: its kind's words, sharpened by the stage it reached, the decision's value or the meeting. */
export function rowWords(row: SentenceRow): readonly LabelDef[] {
  const kind = kindOf(row.kind);
  if (kind === "application.stage_changed") {
    const reached = ownEntry(STAGE_WORDS, code(row.facts, "to_stage"));
    if (reached) return [W.stage, reached];
  }
  if (kind === "application.decision") {
    const value = ownEntry(DECISION_WORDS, code(row.facts, "value"));
    if (value) return [W.candidateDecision, value];
  }
  if (kind.startsWith("scheduling.")) {
    const meeting = ownEntry(MEETING_WORDS, code(row.facts, "meeting_type"));
    const words = wordsFor(kind);
    if (meeting && words.length > 1) return [meeting, ...words.slice(1)];
  }
  return wordsFor(kind);
}

/** His sentence for one row. */
export function sentenceOf(row: SentenceRow): string {
  return rowWords(row).map((word) => word.text).join(SEPARATOR);
}

/** The duty hue of a row (DS §1.4): its last word's, when that is one of his status words; blue otherwise. */
export function toneOfRow(row: SentenceRow): Tone {
  const words = rowWords(row);
  const last = words[words.length - 1]!.text;
  return isStatusWord(last) ? toneOf(last) : "info";
}
